import { useEffect, useState } from 'react';
import { Route, Switch, useHistory } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import Header from '../../components/Header';
import IconIntegrations from '../../components/icons/IconIntegrations';
import Loader from '../../components/Loader';
import { ROUTES } from '../../routes/routes';
import { CompanySettings, Integration, Integrations } from '../../types/settings';
import { useAxios } from '../../utils/hooks';
import BubbleConfiguration from './BubbleConfiguration';
import BubbleEventLogs from './BubbleEventLogs';
import ConfigurationsList from './ConfigurationsList';
import PayPalConfiguration from './PayPalConfiguration';
import RewardfulConfiguration from './RewardfulConfiguration';
import Tabs from '../../components/Tabs';

export default function IntegrationsPage() {
    const axios = useAxios();
    const { addToast } = useToasts();
    const history = useHistory();
    const [settings, setSettings] = useState<CompanySettings>();

    useEffect(() => {
        if (!axios || !axios.current) {
            return;
        }

        axios.current
            .get<CompanySettings>('/company/settings')
            .then(({ data }) => {
                setSettings(data);
            })
            .catch((error) => {
                addToast(error.message, { appearance: 'error' });
            });
    }, [addToast, axios]);

    const handleConfigure = (integration: Integration) => {
        if (integration === Integration.BUBBLE) {
            history.push(ROUTES.INTEGRATIONS_BUBBLE);
        } else if (integration === Integration.PAYPAL) {
            history.push(ROUTES.INTEGRATIONS_PAYPAL);
        } else if (integration === Integration.REWARDFUL) {
            history.push(ROUTES.INTEGRATIONS_REWARDFUL);
        } else {
            addToast('Integration not yet supported', { appearance: 'error' });
        }
    };

    const handleSaveSettings = async (newSettings: CompanySettings) => {
        try {
            const response = await axios?.current?.post<CompanySettings>('/company/settings', newSettings);
            if (response?.data) {
                addToast('Settings saved!', { appearance: 'success' });
                setSettings(response.data);
            } else {
                setSettings(newSettings);
                addToast('Error saving settings', { appearance: 'error' });
            }
        } catch (err: any) {
            console.error(err);
            setSettings(newSettings);
            addToast(err?.response?.data?.error ?? 'Error saving settings', { appearance: 'error' });
        }
    };

    const handleDeleteIntegration = async (type: Integration) => {
        try {
            const response = await axios?.current?.delete<CompanySettings>(`company/settings/integrations/${type}`);
            if (response?.data) {
                addToast('Integration deleted', { appearance: 'success' });
                setSettings(response.data);
            } else {
                addToast('Error saving settings', { appearance: 'error' });
            }
        } catch (err: any) {
            console.error(err);
            addToast(err?.response?.data?.error ?? 'Error saving settings', { appearance: 'error' });
        }
    };

    function ListComponent() {
        return <ConfigurationsList onConfigure={handleConfigure} />;
    }

    function BubbleComponent() {
        if (!settings) {
            return null;
        }

        const handleSave = (integrations: Integrations) =>
            handleSaveSettings({
                ...settings,
                integrations: { bubble: integrations.bubble },
            });

        const handleDelete = () => handleDeleteIntegration(Integration.BUBBLE);

        return (
            <>
                <div className="mb-4 px-4 text-left">
                    <p className="text-lg font-light">
                        Read the Bubble integration{' '}
                        <a
                            href="https://help.pricewell.com/integrations/bubble/"
                            target="_blank"
                            rel="noreferrer"
                            className="link"
                        >
                            instructions here
                        </a>
                        .
                    </p>
                </div>
                <Tabs
                    tabs={[
                        {
                            id: 'configurations',
                            title: 'Configuration',
                            body: (
                                <BubbleConfiguration
                                    configuration={settings.integrations}
                                    onSave={handleSave}
                                    onDelete={handleDelete}
                                />
                            ),
                        },
                        {
                            id: 'logs',
                            title: 'Logs',
                            body: <BubbleEventLogs />,
                        },
                    ]}
                    defaultId={
                        !!settings.integrations.bubble && settings.integrations.bubble.length > 0
                            ? 'logs'
                            : 'configurations'
                    }
                />
            </>
        );
    }

    function PayPalComponent() {
        if (!settings) {
            return null;
        }

        const handleSave = (integrations: Integrations) =>
            handleSaveSettings({
                ...settings,
                integrations: {
                    paypal: integrations.paypal,
                },
            });

        const handleDelete = () => handleDeleteIntegration(Integration.PAYPAL);
        return (
            <PayPalConfiguration configuration={settings.integrations} onSave={handleSave} onDelete={handleDelete} />
        );
    }

    function RewardfulComponent() {
        if (!settings) {
            return null;
        }

        const handleSave = (integrations: Integrations) =>
            handleSaveSettings({
                ...settings,
                integrations: {
                    rewardful: integrations.rewardful,
                },
            });

        const handleDelete = () => handleDeleteIntegration(Integration.REWARDFUL);
        return (
            <RewardfulConfiguration configuration={settings.integrations} onSave={handleSave} onDelete={handleDelete} />
        );
    }

    return (
        <div className="p-4 w-full text-center">
            <Header>
                <IconIntegrations className="inline w-6 mr-3" /> Integrations
                <span className="text-lg font-light ml-3">Integrate with third party tools</span>
            </Header>
            <div className="p-4">
                {!settings && <Loader full />}
                {settings && (
                    <Switch>
                        <Route exact path={ROUTES.INTEGRATIONS} component={ListComponent} />
                        <Route exact path={ROUTES.INTEGRATIONS_BUBBLE} component={BubbleComponent} />
                        <Route exact path={ROUTES.INTEGRATIONS_PAYPAL} component={PayPalComponent} />
                        <Route exact path={ROUTES.INTEGRATIONS_REWARDFUL} component={RewardfulComponent} />
                    </Switch>
                )}
            </div>
        </div>
    );
}
